import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';

import { codelists } from 'constants/codelists';
import { changeMainSwitchValueByNotificationSetId } from 'features/views/settings/actions';
import {
	getMainSwitchDisabledByNotificationSetId,
	getMainSwitchValueByNotificationSetId,
} from 'features/views/settings/selectors';
import { getContextUser, hasContextPermissionById } from 'features/views/userContext/selectors';
import { getCodelistByName, getCodelistPropById } from 'utils';

export const SettingsSwitch = ({
	description,
	notificationSetId,
	disabled,
	value,
	doNotUseCodelistPermissionForSwitch = false,
}) => {
	const dispatch = useDispatch();

	const contextUser = useSelector(getContextUser);

	const notificationsSetCodelist = useSelector(getCodelistByName(codelists.notificationsSet));
	const permissionByNotificationSetId = getCodelistPropById(
		`${notificationSetId}`,
		notificationsSetCodelist || [],
		'permissionId',
	);

	const formattedPermissionId = [{ id: permissionByNotificationSetId }];
	const userPermission = useSelector((state) => hasPermissionByIds(state)(formattedPermissionId));
	const contextUserPermission = useSelector((state) => hasContextPermissionById(state, formattedPermissionId));
	const hasSwitchPermissionFromCodelist = contextUser ? contextUserPermission : userPermission;

	const isSumarryNotificationTasksSwitchEnabled = useSelector((state) =>
		getMainSwitchValueByNotificationSetId(state, notificationSetId),
	);

	const isSummaryNotificationTasksSwitchDisabled = useSelector((state) =>
		getMainSwitchDisabledByNotificationSetId(state, notificationSetId),
	);

	const handleToggleSwitch = useCallback(() => {
		dispatch(changeMainSwitchValueByNotificationSetId(notificationSetId));
	}, [dispatch, notificationSetId]);

	const element = (
		<Stack
			direction="row"
			spacing={1}
			sx={{
				alignItems: 'center',
			}}
		>
			<Switch
				checked={value || isSumarryNotificationTasksSwitchEnabled}
				onChange={handleToggleSwitch}
				disabled={disabled || isSummaryNotificationTasksSwitchDisabled}
			/>
			<Typography variant="subtitle" color="text.primary">
				{description}
			</Typography>
		</Stack>
	);

	return doNotUseCodelistPermissionForSwitch ? element : hasSwitchPermissionFromCodelist && element;
};

SettingsSwitch.propTypes = {
	description: PropTypes.string,
	disabled: PropTypes.bool,
	doNotUseCodelistPermissionForSwitch: PropTypes.bool,
	notificationSetId: PropTypes.number,
	value: PropTypes.bool,
};

export default SettingsSwitch;
